import { createContext, ReactNode, useContext, useRef } from "react"
import { create, useStore } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"
import { UnsentChatMessage } from "@/modules/chats/types/chat-message"

interface UnsentMessage {
  unsentMessages: Record<string, UnsentChatMessage | null>
  setUnsentMessage: (threadId: string, unsentMessage: UnsentChatMessage | null) => void
  getUnsentMessage: (threadId: string) => UnsentChatMessage | undefined | null
}

const createUnsentMessageStore = () => {
  return create<UnsentMessage>()(
    persist(
      (set, get) => ({
        unsentMessages: {},
        getUnsentMessage: (threadId) => get().unsentMessages[threadId],
        setUnsentMessage: (threadId, unsentMessage) => {
          set({ unsentMessages: { ...get().unsentMessages, [threadId]: unsentMessage } })
        },
      }),
      { name: "unsentMessageStore", storage: createJSONStorage(() => sessionStorage) },
    ),
  )
}

type UnsentMessageStore = ReturnType<typeof createUnsentMessageStore>

const UnsentMessageStoreContext = createContext<UnsentMessageStore | null>(null)

const UnsentMessageProvider = ({ children }: { children: ReactNode }) => {
  const storeRef = useRef<UnsentMessageStore>(createUnsentMessageStore())

  return <UnsentMessageStoreContext.Provider value={storeRef.current}>{children}</UnsentMessageStoreContext.Provider>
}

function useUnsentMessageContext<T>(selector: (state: UnsentMessage) => T): T {
  const store = useContext(UnsentMessageStoreContext)
  if (!store) throw new Error("Missing UnsentMessageStoreContext.Provider in the tree")

  return useStore(store, selector)
}

export { createUnsentMessageStore, UnsentMessageProvider, useUnsentMessageContext }
